import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MoreHorizontal } from '@lyearn/icons';
import { ActionMenu } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Stack, Typography } from '@lyearn/ui';
import { FilterOperatorEnum, GroupTypeEnum } from '@/types/schema';

import useFilters from '../../hooks/useFilters';
import { ConditionalFieldsMapping, FilterInputsD1, GroupConditionAction } from '../../types';
import { FilterItemOverrides, FilterItemProps } from '../FilterItem';
import Filters from '../Filters';
import OperatorSelect from '../OperatorSelect';
import { OperatorSelectVariant } from '../OperatorSelect/type';
import AddFilter, { AddFilterProps } from './AddFilter';
import { getMenuItems } from './utils';

export interface AddedConditionProps {
  filter: FilterInputsD1;
  onGroupChange: (item: FilterInputsD1, index: number) => void;
  index: number;
  onConditionOperatorChange: (val: FilterOperatorEnum) => void;
  conditionOperator: FilterOperatorEnum;
  error?: boolean;
  onGroupDelete: (index: number) => void;
  onGroupDuplicate: (index: number) => void;
  type: GroupTypeEnum;
  filterItemOverrides?: FilterItemOverrides;
  RenderFilterItem?: (params: FilterItemProps) => React.JSX.Element;
  fieldsMapping?: ConditionalFieldsMapping[];
  disabled?: boolean;
  classes?: {
    conditionGroupContainer?: string;
    filterContainer?: string;
    actionMenu?: string;
    divider?: string;
    operatorSelector?: string;
    operatorSelectorDivider?: string;
    operatorHeading?: string;
    addFilter?: AddFilterProps['classes'];
  };
  showPlaceholderIcon?: boolean;
  order?: number;
}

function AddedCondition(props: AddedConditionProps) {
  const { t } = useTranslation();
  const {
    filter,
    onGroupChange,
    index,
    onConditionOperatorChange,
    conditionOperator,
    error,
    onGroupDelete,
    onGroupDuplicate,
    type,
    filterItemOverrides,
    RenderFilterItem,
    disabled,
    classes,
    fieldsMapping,
    showPlaceholderIcon,
    order,
  } = props;
  const { operator } = filter;
  const menuItems = useMemo(() => getMenuItems(t), [t]);

  const { data, methods } = useFilters({
    filter,
    onGroupChange,
    index,
  });

  const { filtersWithTempId } = data;
  const { onFilterChange, onOperatorChange, onAddFilter, onRemoveFilter } = methods;
  const onAction = useCallback(
    (value: string) => {
      const actionType = value as GroupConditionAction;
      if (actionType === GroupConditionAction.Delete) {
        onGroupDelete(index);
      } else if (actionType === GroupConditionAction.Duplicate) {
        onGroupDuplicate(index);
      }
    },
    [index, onGroupDelete, onGroupDuplicate],
  );

  return (
    <>
      {index > 0 ? (
        <>
          <Box className="w-min">
            <OperatorSelect
              className={cn(
                '-translate-x-[0.8rem] transform rtl:translate-x-[0.8rem]',
                classes?.operatorSelector,
              )}
              disabled={disabled}
              selectedValue={conditionOperator}
              onChange={onConditionOperatorChange}
            />
          </Box>
          <Box
            className={cn(
              'h-24 w-1 border-dashed border-interactive-secondary-hover ms-16 border-s',
              classes?.operatorSelectorDivider,
            )}
          />
        </>
      ) : null}
      <Stack
        alignItems="start"
        className={cn(
          'relative -mx-16 rounded-8 border border-interactive-secondary px-16 py-20 hover:shadow-01',
          classes?.conditionGroupContainer,
        )}
        direction="col">
        <Stack alignItems="center" className="ml-8">
          <Typography
            className={classes?.operatorHeading}
            color="text-text-paragraph"
            variant="body-short-01">
            {t('Group:Editor.Match')}
          </Typography>
          <OperatorSelect
            className="mx-8"
            disabled={disabled}
            selectedValue={operator}
            variant={OperatorSelectVariant.AnyAll}
            onChange={onOperatorChange}
          />
          <Typography
            className={classes?.operatorHeading}
            color="text-text-paragraph"
            variant="body-short-01">
            {t('Group:Editor.OfTheFollowing')}
          </Typography>
        </Stack>
        <Filters
          RenderFilterItem={RenderFilterItem}
          className={classes?.filterContainer}
          disabled={disabled}
          error={error}
          fieldsMapping={fieldsMapping}
          filterItemOverrides={filterItemOverrides}
          filters={filtersWithTempId}
          order={order}
          showPlaceholderIcon={showPlaceholderIcon}
          type={type}
          onChange={onFilterChange}
          onRemoveFilter={onRemoveFilter}
        />

        {disabled ? null : (
          <>
            <AddFilter classes={classes?.addFilter} onClick={onAddFilter} />
            <Box className={cn('absolute top-16 end-16', classes?.actionMenu)}>
              <ActionMenu items={menuItems} onAction={onAction}>
                <LinkButton className="h-24 w-24" color={'internal'}>
                  <MoreHorizontal className="h-24 w-24" />
                </LinkButton>
              </ActionMenu>
            </Box>
          </>
        )}
      </Stack>
      <Box
        className={cn(
          'h-24 w-1 border-dashed border-interactive-secondary-hover ms-16 border-s',
          classes?.divider,
        )}
      />
    </>
  );
}

export default AddedCondition;
